import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CountryItem } from './types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CountryDialogFields } from './dialog/CountryDialogFields';
import { CountryImageUpload } from './dialog/CountryImageUpload';
import { CountrySocialMediaFields } from './dialog/CountrySocialMediaFields';
import { CountryVideoFields } from './dialog/CountryVideoFields';
import { SocialPlatform } from '@/types/socialPlatform';
import { VideoConfig } from '@/types/videoConfig';
import { ScrollArea } from '@/components/ui/scroll-area';
import CountryFlag from '@/components/country/CountryFlag';
import { useIsMobile } from '@/hooks/use-mobile';

interface CountryEditDialogProps {
  country: CountryItem | null;
  open: boolean;
  onClose: () => void;
  onSave: (country: CountryItem) => void;
}

export const CountryEditDialog = ({ country, open, onClose, onSave }: CountryEditDialogProps) => {
  const [editedCountry, setEditedCountry] = useState<CountryItem | null>(country);
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    setEditedCountry(country);
  }, [country]);

  if (!editedCountry) return null;

  const handleSave = async () => {
    setUploading(true);
    try {
      let imageUrl = editedCountry.background_image_url;
      let imageAlt = editedCountry.background_image_alt;

      // If there's a new image file to upload
      if (backgroundImageFile) {
        const fileExt = backgroundImageFile.name.split('.').pop();
        const fileName = `${editedCountry.code}_hero_${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        // Upload the image to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('country-assets')
          .upload(filePath, backgroundImageFile, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) {
          toast.error('Failed to upload image');
          console.error(uploadError);
          return;
        }

        // Get the public URL for the uploaded image
        const { data: { publicUrl } } = supabase.storage
          .from('country-assets')
          .getPublicUrl(filePath);

        console.log('Uploaded image URL:', publicUrl);
        imageUrl = publicUrl;
        imageAlt = imageAlt || `Background for ${editedCountry.name}`;
      }

      // Prepare the final country object with potentially updated image data
      const finalCountry = {
        ...editedCountry,
        background_image_url: imageUrl,
        background_image_alt: imageAlt,
      };

      // Update the country record in the database
      const { error: updateError } = await supabase
        .from('countries')
        .update({ 
          name: finalCountry.name,
          active: finalCountry.active,
          background_image_url: finalCountry.background_image_url,
          background_image_alt: finalCountry.background_image_alt,
          social_media: finalCountry.social_media as unknown as any, // Cast through unknown to avoid TypeScript errors
          featured_video: finalCountry.featured_video as unknown as any // Similarly cast the featured video field
        })
        .eq('code', finalCountry.code);
        
      if (updateError) {
        toast.error('Failed to update country');
        console.error('Database update error:', updateError);
        return;
      }

      console.log('Country updated with image:', finalCountry);
      
      // Call the onSave callback with the updated country data
      onSave(finalCountry);
      toast.success(`${finalCountry.name} updated successfully`);
    } catch (err) {
      toast.error('An error occurred while saving changes');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (file: File | null) => {
    setBackgroundImageFile(file);
    if (file) {
      setEditedCountry({ 
        ...editedCountry, 
        background_image_alt: `Background for ${editedCountry.name}` 
      });
    }
  };

  const handleImageRemove = () => {
    setBackgroundImageFile(null);
    setEditedCountry({ 
      ...editedCountry, 
      background_image_url: null, 
      background_image_alt: null 
    });
  };

  const handleSocialMediaChange = (platforms: SocialPlatform[]) => {
    setEditedCountry({
      ...editedCountry,
      social_media: platforms
    });
  };

  const handleVideoChange = (videoConfig: VideoConfig) => {
    setEditedCountry({
      ...editedCountry,
      featured_video: videoConfig
    });
  };

  // Determine if we have an image (either existing or newly selected)
  const hasImage = !!(editedCountry.background_image_url || backgroundImageFile);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="space-y-2 md:space-y-3">
          <div className="flex items-center space-x-2">
            <CountryFlag countryCode={editedCountry.code} className={`${isMobile ? "h-5 w-7" : "h-6 w-8"}`} />
            <DialogTitle className={`${isMobile ? "text-lg" : "text-xl"}`}>{editedCountry.name}</DialogTitle>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            Edit country details and manage hero image
          </p>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto pr-4 max-h-[calc(80vh-100px)]">
          <div className="space-y-4">
            <CountryDialogFields
              country={editedCountry}
              onCountryChange={setEditedCountry}
            />

            <CountryImageUpload
              hasImage={hasImage}
              imageUrl={editedCountry.background_image_url}
              onImageSelect={handleImageSelect}
              onImageRemove={handleImageRemove}
            />

            <CountrySocialMediaFields 
              socialPlatforms={editedCountry.social_media || []}
              onChange={handleSocialMediaChange}
            />
            
            <CountryVideoFields 
              videoConfig={editedCountry.featured_video}
              onChange={handleVideoChange}
            />
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={uploading}>
            {uploading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
